import { PaymentType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { formatPrice } from '@allesauto/utils/funcs';
import clsx from 'clsx';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { Maybe } from 'yup/lib/types';

export interface PDPFooterPriceProps {
  paymentType?: PaymentType;
  price?: SchemaTypes.Vehicle['price'];
  monthlyInstallment?: number;
  isPrivateSeller?: boolean | null;
  isFixedPrice?: boolean | null;
  className?: string;
  withFractionDigits?: boolean;
  priceLabel?: string;
  shipmentPrice?: Maybe<number> | undefined;
}

const PDPFooterPrice: FC<PDPFooterPriceProps> = ({
  paymentType,
  price,
  monthlyInstallment,
  isPrivateSeller = false,
  isFixedPrice = false,
  withFractionDigits = false,
  className: customClassName,
  priceLabel,
  shipmentPrice,
}) => {
  const intl = useIntl();

  const className = {
    container: clsx('flex flex-col items-end', customClassName),
    mainPriceClass: clsx(priceLabel && 'self-center'),
  };

  return (
    <div className={className.container}>
      {!isPrivateSeller &&
        (paymentType === PaymentType.LeasingRateRange ||
          paymentType === PaymentType.LoanRateRange) && (
          <>
            <div className="flex flex-row">
              <span
                className="flex footer-price-rate items-center pr-10px text-center text-green60 sm:text-p3 md:text-p2 monochrome:text-white"
                id="label--payment-type"
              >
                {priceLabel
                  ? priceLabel
                  : translate(intl, 'ui.PDP.FOOTER.LABEL_TOTAL_PRICE')}
              </span>
              <span
                className="font-headline text-green60 text-headline2 monochrome:text-white"
                id="label--price-monthly-value"
              >
                {formatPrice(intl, monthlyInstallment, {
                  withFractionDigits: true,
                })}
              </span>
            </div>
            {price && (
              <p>
                <span
                  className="font-headline text-p2 text-white"
                  id="label--price-value"
                >
                  {formatPrice(intl, price, {
                    withFractionDigits,
                  })}
                </span>
              </p>
            )}
          </>
        )}
      {!isPrivateSeller && paymentType === PaymentType.CashRange && (
        <>
          {price && (
            <p
              className="flex flex-row font-headline text-green60 sm:text-headline4 md:text-headline2 monochrome:text-white"
              id="label--price-value"
            >
              {priceLabel && (
                <span className="flex font-regular font-weight-normal footer-price-rate pr-10px text-green60 text-right sm:text-p3 md:text-p2 monochrome:text-white">
                  {priceLabel}
                </span>
              )}
              <span className={className.mainPriceClass}>
                {shipmentPrice
                  ? formatPrice(intl, price + shipmentPrice, {
                      withFractionDigits,
                    })
                  : formatPrice(intl, price, {
                      withFractionDigits,
                    })}
              </span>
            </p>
          )}
          {monthlyInstallment && (
            <p className="flex flex-row">
              <span
                className="font-weight-normal pr-[7px] text-p2 text-white wl-custom-text-highlight"
                id="label--payment-type"
              >
                {translate(intl, 'ui.PDP.FOOTER.LABEL_INSTALLMENT')}
              </span>
              <span
                className="font-headline text-p2 text-white wl-custom-text-highlight monochrome:text-white"
                id="label--price-monthly-value"
              >
                {formatPrice(intl, monthlyInstallment, {
                  withFractionDigits: true,
                })}
              </span>
            </p>
          )}
        </>
      )}
      {isPrivateSeller && (
        <>
          <p
            className="font-headline text-green60 sm:text-headline4 md:text-headline2 monochrome:text-white"
            id="label--price-value"
          >
            {formatPrice(intl, price, {
              withFractionDigits,
            })}
          </p>
          <p
            className="font-weight-bold text-p2 text-white"
            id="label--payment-type"
          >
            {translate(
              intl,
              isFixedPrice
                ? 'ui.PDP.C2C.FIXED_PRICE'
                : 'ui.PDP.C2C.NEGOTIABLE_PRICE'
            )}
          </p>
        </>
      )}
    </div>
  );
};

export default PDPFooterPrice;
